import type {
  CollectionRules,
  CollectionRulesInput,
  RuleConditionInput,
} from '@nucleus-commerce/api-client/admin';

import { isListOperator, type RuleValueKind } from '../schemas/rule-fields';
import type { RuleConditionForm, RulesForm } from '../schemas/rules-form-schema';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function toRuleScalar(value: string, kind: RuleValueKind): string | number | boolean {
  switch (kind) {
    case 'money':
    case 'number':
      return Number(value);
    case 'boolean':
      return value === 'true';
    case 'date':
      return DATE_ONLY.test(value) ? `${value}T00:00:00.000Z` : value;
    case 'text':
    case 'tag':
    case 'enum':
      return value;
  }
}

function fromRuleScalar(value: string | number | boolean, kind: RuleValueKind): string {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (kind === 'date' && value.length >= 10) return value.slice(0, 10);
  return value;
}

/** Form → API. `kindOf` resolves how a field's value is typed (money, date, boolean...). */
export function toRulesInput(
  rules: RulesForm,
  kindOf: (field: string) => RuleValueKind,
): CollectionRulesInput {
  return {
    match: rules.match,
    conditions: rules.conditions.map((c): RuleConditionInput => {
      const kind = kindOf(c.field);
      const value = Array.isArray(c.value)
        ? c.value.map((v) => toRuleScalar(v, kind))
        : toRuleScalar(c.value, kind);
      return { field: c.field, operator: c.operator, value };
    }),
  };
}

export function fromRules(
  rules: CollectionRules | null,
  kindOf: (field: string) => RuleValueKind,
): RulesForm {
  if (!rules) return { match: 'all', conditions: [] };
  return {
    match: rules.match,
    conditions: rules.conditions.map((c): RuleConditionForm => {
      const kind = kindOf(c.field);
      const raw = c.value;
      const value = Array.isArray(raw)
        ? raw.map((v) => fromRuleScalar(v, kind))
        : isListOperator(c.operator)
          ? [fromRuleScalar(raw, kind)]
          : fromRuleScalar(raw, kind);
      return { field: c.field, operator: c.operator, value };
    }),
  };
}

/** Whether every condition is complete enough to be previewed. */
export function rulesArePreviewable(rules: RulesForm): boolean {
  return (
    rules.conditions.length > 0 &&
    rules.conditions.every(
      (c) =>
        c.field !== '' && (Array.isArray(c.value) ? c.value.length > 0 : c.value.trim() !== ''),
    )
  );
}
